import React, { useEffect } from "react"
import {
  UserIcon,
  ShoppingBagIcon,
  ArrowDownTrayIcon,
  KeyIcon,
  DocumentTextIcon,
  MapPinIcon,
  PuzzlePieceIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline"
import { usePageContext } from "vike-react/usePageContext"
import useClientAuth from "../hooks/useClientAuth"
import Typography from "aldoo-ra/Typography"
import Tabs from "aldoo-ra/Tabs"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import tabsVariants from "../../Variants/Tabs"
import { navigate } from "vike/client/router"
import Spinner from "../ui/Spinner"
import useT from "../hooks/useT"
import { usePageTitle } from "../hooks/usePageTitle"
import { stripLang } from "../utils/contentRoutes"
import Profile from "../account/Profile"
import Orders from "../account/Orders"
import DigitalProducts from "../account/DigitalProducts"
import SoftwareLicenses from "../account/SoftwareLicenses"
import Invoices from "../account/Invoices"
import Addresses from "../account/Addresses"
import AddOns from "../account/AddOns"
import Privacy from "../account/Privacy"
import Welcome from "../account/Welcome"
import clientRedirect from "../utils/clientRedirect"
import { useSignInGate } from "../utils/signIn"

const SECTIONS = [
  { id: "profile", icon: UserIcon, label: "Profile", Component: Profile },
  { id: "orders", icon: ShoppingBagIcon, label: "Orders", Component: Orders },
  { id: "digital-products", icon: ArrowDownTrayIcon, label: "Digital Products", Component: DigitalProducts },
  { id: "software-licenses", icon: KeyIcon, label: "Software Licenses", Component: SoftwareLicenses },
  { id: "invoices", icon: DocumentTextIcon, label: "Invoices", Component: Invoices },
  { id: "addresses", icon: MapPinIcon, label: "Addresses", Component: Addresses },
  // add-ons are not offered in the store's menu for now; the page still
  // opens by url (the old links keep working)
  { id: "add-ons", icon: PuzzlePieceIcon, label: "Add-ons", Component: AddOns, hidden: true },
  { id: "privacy", icon: ShieldCheckIcon, label: "Privacy", Component: Privacy },
]

/**
 * /user[/:section[/:sub]] — the account area. Sections render client-side
 * only (they need the signed-in credentials); guests are sent to sign-in.
 */
export default function UserPage() {
  const t = useT()
  const pageContext = usePageContext()
  const { auth } = useClientAuth()
  const segments = stripLang(pageContext.urlPathname).split("/").filter(Boolean)
  const welcome = segments[1] === "welcome"
  const requested = welcome ? "profile" : segments[1]
  const section = SECTIONS.find((s) => s.id === requested) || SECTIONS[0]

  // unknown section → /user (old User/index.jsx)
  useEffect(() => {
    if (segments[1] && !welcome && !SECTIONS.some((s) => s.id === segments[1])) clientRedirect("/user")
  }, [segments[1]])
  const sub = segments[2]
  usePageTitle(`${t(section.label)} | ${t`My account`}`)

  // a guest signs in over this page; closing it goes home
  useSignInGate(auth)

  const signedIn = !!auth?.auth_token
  const Component = section.Component
  const tabs = SECTIONS.filter((entry) => !entry.hidden || entry.id === section.id).map(({ id, icon: Icon, label }) => ({ value: id, label: t(label), icon: <Icon /> }))
  const open = (id) => navigate(id === "profile" ? "/user" : `/user/${id}`)

  return (
    <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 pt-8 md:pt-12 pb-24 flex flex-col md:flex-row gap-4 lg:gap-8" data-testid="user-page">
      <nav className="md:w-[260px] shrink-0" aria-label={t`My account`}>
        <Typography 
          component="h1" 
          variant="h5" 
          className="hidden md:block mb-2"
        >
          {t`My account`}
        </Typography>
        {/* on a phone the sections are a dropdown, its options a bottom sheet
            (as the merchant portal's sub-tabs, AdminVendors/SectionPicker) */}
        <div className="md:hidden" data-testid="account-sections-select">
          <DropdownSelect
            variant="aldoo"
            options={tabs}
            value={tabs.filter((x) => x.value === section.id)}
            nameField="label"
            showCheckbox={false}
            showSelectionTick
            searchEnabled={false}
            enableResetButton={false}
            allowEmptySelection={false}
            optionsAsBottomDrawer
            dialogTitle={t`My account`}
            onChange={(sel) => {
              const picked = Array.isArray(sel) ? sel[0] : sel
              if (picked?.value) open(picked.value)
            }}
            onItemRender={(x) => {
              const item = Array.isArray(x) ? x[0] : x
              return (
                <span className="flex items-center gap-2 [&>svg]:w-5 [&>svg]:h-5 [&>svg]:shrink-0">
                  {item?.icon}
                  {item?.label}
                </span>
              )
            }}
          />
        </div>
        <div className="hidden md:block">
          <Tabs
            variant="sf_account"
            // the variant's own classes, handed over as well: a variant file
            // added while the dev server runs is not in its glob until a restart
            styleOverrides={tabsVariants.sf_account}
            value={section.id}
            onChange={open}
            tabs={tabs}
          />
        </div>
      </nav>
      <div className="flex-1 min-w-0">
        {signedIn ? <Component sub={sub} /> : <Spinner className="py-24" />}
        {signedIn && welcome && <Welcome />}
      </div>
    </div>
  )
}
